import { notifications } from '@mantine/notifications';
import DOMPurify from 'dompurify';
import * as React from 'react';

const normalizeWhitespace = (value: string): string => value.replace(/\s+/g, ' ').trim();

const normalizeHrefForText = (href: string): string => {
  if (href.startsWith('mailto:')) {
    return href.replace('mailto:', '');
  }

  if (href.startsWith('tel:')) {
    return href.replace('tel:', '');
  }

  return href;
};

const shouldAppendHref = (text: string, href: string): boolean => {
  const normalizedText = text.trim();
  const normalizedHref = normalizeHrefForText(href);

  if (!normalizedHref || normalizedHref.startsWith('#')) {
    return false;
  }

  if (!normalizedText) {
    return true;
  }

  if (normalizedText === normalizedHref) {
    return false;
  }

  return !normalizedText.includes(normalizedHref);
};

type PlainTextRoot = DocumentFragment | Element;

const enhanceAnchorsForPlainText = (root: PlainTextRoot) => {
  root.querySelectorAll('a[href]').forEach((anchor) => {
    const href = anchor.getAttribute('href') ?? '';
    const normalizedHref = normalizeHrefForText(href);

    if (!normalizedHref) {
      return;
    }

    const currentText = (anchor.textContent ?? '').trim();

    if (!currentText) {
      anchor.textContent = normalizedHref;

      return;
    }

    if (shouldAppendHref(currentText, normalizedHref)) {
      anchor.textContent = `${currentText} (${normalizedHref})`;
    }
  });
};

const escapeMarkdownCell = (value: string): string => value.replace(/\|/g, '\\|');

const formatMarkdownRow = (cells: string[]): string => `| ${cells.map(escapeMarkdownCell).join(' | ')} |`;

const tableToPlainText = (table: HTMLTableElement): string => {
  const rows = Array.from(table.querySelectorAll('tr'));

  if (!rows.length) {
    return '';
  }

  const rowCells = rows.map((row) =>
    Array.from(row.querySelectorAll('th,td')).map((cell) => normalizeWhitespace(cell.textContent ?? '')),
  );
  const maxCols = Math.max(0, ...rowCells.map((row) => row.length));

  if (!maxCols) {
    return '';
  }

  const padRow = (row: string[]): string[] => row.concat(Array(Math.max(0, maxCols - row.length)).fill(''));

  let headerRow: string[] | null = null;
  let bodyRows = rowCells;

  const theadRow = table.tHead?.rows[0];

  if (theadRow) {
    const headIndex = rows.indexOf(theadRow);

    if (headIndex >= 0) {
      headerRow = padRow(rowCells[headIndex]);
      bodyRows = rowCells.filter((_, index) => index !== headIndex);
    }
  } else if (rows[0].querySelectorAll('th').length > 0) {
    headerRow = padRow(rowCells[0]);
    bodyRows = rowCells.slice(1);
  }

  if (!headerRow) {
    headerRow = padRow(rowCells[0]);
    bodyRows = rowCells.slice(1);
  }

  const divider = Array(maxCols).fill('---');
  const lines = [formatMarkdownRow(headerRow), formatMarkdownRow(divider)];

  bodyRows.forEach((row) => {
    lines.push(formatMarkdownRow(padRow(row)));
  });

  return lines.join('\n');
};

const replaceTablesWithPlainText = (root: PlainTextRoot) => {
  const tables = Array.from(root.querySelectorAll('table'));
  const ownerDocument = root.ownerDocument ?? document;

  tables.forEach((table) => {
    const plainText = tableToPlainText(table);

    if (!plainText) {
      return;
    }

    const textNode = ownerDocument.createTextNode(`\n${plainText}\n`);

    table.replaceWith(textNode);
  });
};

const parseHtmlFragment = (html: string): DocumentFragment | null => {
  if (!html || typeof document === 'undefined' || typeof DOMParser === 'undefined') {
    return null;
  }

  const sanitizedHtml = DOMPurify.sanitize(html);
  const parser = new DOMParser();
  const parsed = parser.parseFromString(sanitizedHtml, 'text/html');
  const fragment = document.createDocumentFragment();

  Array.from(parsed.body.childNodes).forEach((node) => {
    fragment.appendChild(document.importNode(node, true));
  });

  return fragment;
};

const normalizePlainTextOutput = (value: string): string =>
  value
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const htmlToPlainText = (html?: string): string => {
  if (!html) {
    return '';
  }

  const root = parseHtmlFragment(html);

  if (!root) {
    return '';
  }

  enhanceAnchorsForPlainText(root);
  replaceTablesWithPlainText(root);

  const text = root.textContent ?? '';

  return normalizePlainTextOutput(text);
};

const TABLE_COPY_STYLE =
  '<style data-table-copy-style>table{border-collapse:collapse;width:100%;margin:12px 0;}th,td{border:1px solid #d4d8e3;padding:6px 8px;vertical-align:top;text-align:left;}th{background:#f6f8fb;font-weight:600;}</style>';

const withTableStyles = (html: string): string => {
  if (!html || !html.includes('<table')) {
    return html;
  }

  if (html.includes('data-table-copy-style')) {
    return html;
  }

  return `${TABLE_COPY_STYLE}${html}`;
};

const notifyManualCopy = () => {
  notifications.show({
    id: 'manual-copy',
    color: 'blue',
    title: 'Автокопирование недоступно',
    message: 'Выделите текст на странице и скопируйте вручную (Ctrl/Cmd+C или долгий тап → «Копировать»).',
    autoClose: 6000,
  });
};

export const useCopyFormattedText = (getHtml: () => string, getFallbackPlainText?: () => string) => {
  return React.useCallback(async () => {
    const rawHtml = getHtml();
    const html = withTableStyles(rawHtml);
    const fallbackPlainText = getFallbackPlainText?.() || '';
    const plainText = htmlToPlainText(rawHtml) || normalizePlainTextOutput(fallbackPlainText);

    if (!plainText && !html) {
      return;
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.write && html) {
        const items = [
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plainText], { type: 'text/plain' }),
          }),
        ];

        await navigator.clipboard.write(items);

        return;
      }
    } catch {
      // continue to other strategies
    }

    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText && plainText) {
        await navigator.clipboard.writeText(plainText);

        return;
      }
    } catch {
      // continue to textarea fallback
    }

    if (plainText) {
      notifyManualCopy();
    }
  }, [getFallbackPlainText, getHtml]);
};
